import { Form, TextField, SubmitFormButton } from '@calendly/form';
import { object, string } from '@calendly/form/validation';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { AddMiniIcon, CheckCircleIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addAdditionalInvitee } from 'actions/polls/scheduling';
import { useBooleanState } from 'hooks/useBooleanState';
import { useToggleState } from 'hooks/useToggleState';
import { pollBookingSelector } from 'selectors/scheduled_events';
const flexContainerCss = "feqx1hj";
const FlexContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexContainerCss, props.className)} />;
});
const formControlCss = "fgr222h";
const FormControl = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(formControlCss, props.className)} />;
});
const buttonTextCss = "b1tsli96";
const ButtonText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(buttonTextCss, props.className)} />;
});
const textFieldsContainerIsPhoneCss = "t1h20zf8";
const textFieldsContainerCss = "t1vtg5bk";
const TextFieldsContainer = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <FlexContainer ref={ref} {...props} className={classNames(textFieldsContainerCss, {
    [textFieldsContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledTextFieldIsPhoneCss = "s85pwvu";
const styledTextFieldCss = "sz56klg";
const StyledTextField = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <TextField ref={ref} {...props} className={classNames(styledTextFieldCss, {
    [styledTextFieldIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledButtonCss = "suiyh5v";
const StyledButton = forwardRef((props, ref) => <MinimalButton ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
const addInviteeSuccessContainerFadeOutCss = "a1r69o26";
const addInviteeSuccessContainerCss = "a12jud4c";
const AddInviteeSuccessContainer = forwardRef((props, ref) => {
  const {
    fadeOut,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(addInviteeSuccessContainerCss, {
    [addInviteeSuccessContainerFadeOutCss]: fadeOut
  }, props.className)} />;
});
const inviteeAddedTextCss = "i4q5mzr";
const InviteeAddedText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(inviteeAddedTextCss, props.className)} />;
});
const errorMessageCss = "e1gz3qnp";
const ErrorMessage = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(errorMessageCss, props.className)} />;
});
const AddInviteeForm = ({
  toggleOpen,
  showSuccess,
  invitees,
  additionalInvitees
}) => {
  const dispatch = useDispatch();
  const additionalInviteeEmails = additionalInvitees.map(invitee => invitee.email);
  const inviteeEmails = invitees.map(invitee => invitee.email);
  const addInvitee = invitee => dispatch(addAdditionalInvitee(invitee));
  const initialValues = {
    full_name: '',
    email: ''
  };
  const validationSchema = object({
    full_name: string().required(),
    email: string().email().required().test('email', I18n.t('publisher.scheduled_events.pending.booking_modal.duplicate_email_error'), value => ![...additionalInviteeEmails, ...inviteeEmails].includes(value))
  });
  return <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={values => {
    addInvitee(values);
    showSuccess();
    toggleOpen();
  }}>
      <TextFieldsContainer>
        <StyledTextField name="full_name" type="full_name" label={I18n.t('publisher.organization.headers.name')} required />
        <StyledTextField name="email" type="email" label={I18n.t('publisher.labels.email')} required />
      </TextFieldsContainer>
      <FormControl>
        <Button decoration="ghost" size="small" onClick={toggleOpen}>
          {I18n.t('publisher.action.cancel')}
        </Button>
        <SubmitFormButton size="small">
          {I18n.t('publisher.scheduled_events.pending.booking_modal.add_invitee')}
        </SubmitFormButton>
      </FormControl>
    </Form>;
};
const MaxInviteesErrorMessage = () => <ErrorMessage>
    {I18n.t('publisher.scheduled_events.pending.booking_modal.max_invitees_error')}
  </ErrorMessage>;
const AddInviteeControl = ({
  handleClick,
  success,
  hideSuccess
}) => <FlexContainer>
    <StyledButton onClick={handleClick}>
      <AddMiniIcon />
      <ButtonText>
        {I18n.t('publisher.scheduled_events.pending.booking_modal.add_invitee')}
      </ButtonText>
    </StyledButton>
    <AddInviteeSuccessContainer fadeOut={success} onAnimationEnd={hideSuccess}>
      <CheckCircleIcon spacingRight={8} />
      <InviteeAddedText>
        {I18n.t('publisher.scheduled_events.pending.booking_modal.invitee_added')}
      </InviteeAddedText>
    </AddInviteeSuccessContainer>
  </FlexContainer>;
export const AddInvitee = () => {
  const INVITEES_LIMIT = 40;
  const [isOpen, toggleOpen] = useToggleState(true);
  const [success, showSuccess, hideSuccess] = useBooleanState(false);
  const [showMaxInviteesError, setShowMaxInviteesError] = useState(false);
  const {
    invitees,
    additionalInvitees
  } = useSelector(pollBookingSelector);
  const maxInviteesReached = invitees.length + additionalInvitees.length >= INVITEES_LIMIT;
  const handleClick = () => {
    if (maxInviteesReached) {
      setShowMaxInviteesError(true);
    } else {
      hideSuccess();
      toggleOpen();
    }
  };
  useEffect(() => {
    if (!maxInviteesReached) setShowMaxInviteesError(false);
  }, [maxInviteesReached]);
  return <>
      <div>
        {isOpen ? <AddInviteeControl handleClick={handleClick} success={success} hideSuccess={hideSuccess} /> : <AddInviteeForm toggleOpen={toggleOpen} showSuccess={showSuccess} invitees={invitees} additionalInvitees={additionalInvitees} />}
      </div>
      {showMaxInviteesError && <MaxInviteesErrorMessage />}
    </>;
};

require("./add_invitee.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./add_invitee.js");